import { useCallback, useMemo } from 'react'
import {
  FormItemValue,
  PreviousValueEntry
} from '@healthnz-ult/fhir-engine-renderer/types'

interface StoredEntry {
  expiry: number
  value: FormItemValue
}

type StoredEntries = Record<string, StoredEntry>

export interface PreviousValues {
  // TTL-filtered map ready to pass to the renderer as `previousValues`
  data: Record<string, FormItemValue>
  // persist one field's value with its own expiry
  savePreviousValue: (entry: PreviousValueEntry) => void
}

function readEntries(key: string): StoredEntries {
  const itemStr = localStorage.getItem(key)

  if (!itemStr) return {}

  try {
    return JSON.parse(itemStr) as StoredEntries
  } catch {
    return {}
  }
}

// Load the map, drop entries whose TTL has passed (cleaning them out of
// storage), and return `linkId -> value` for the survivors.
function getValidEntries(key: string): Record<string, FormItemValue> {
  const entries = readEntries(key)
  const now = new Date().getTime()

  const valid: Record<string, FormItemValue> = {}
  let changed = false

  for (const [linkId, entry] of Object.entries(entries)) {
    if (now > (entry.expiry ?? 0)) {
      changed = true
      continue
    }
    valid[linkId] = entry.value
  }

  if (changed) {
    if (Object.keys(valid).length === 0) {
      localStorage.removeItem(key)
    } else {
      const survivors: StoredEntries = {}
      for (const linkId of Object.keys(valid)) {
        survivors[linkId] = entries[linkId]
      }
      localStorage.setItem(key, JSON.stringify(survivors))
    }
  }

  return valid
}

export function usePreviousValues(questionnaireId: string): PreviousValues {
  const localStorageKey = `previousValues-${questionnaireId}`

  const data = useMemo(
    () => getValidEntries(localStorageKey),
    [localStorageKey]
  )

  const savePreviousValue = useCallback(
    ({ linkId, value, ttlSeconds }: PreviousValueEntry) => {
      const entries = readEntries(localStorageKey)

      entries[linkId] = {
        value,
        expiry: new Date().getTime() + ttlSeconds * 1000
      }

      localStorage.setItem(localStorageKey, JSON.stringify(entries))
    },
    [localStorageKey]
  )

  return {
    data,
    savePreviousValue
  }
}
